/**
 * Which connectors the grid actually offers.
 *
 * Membership means "has a working connector here". A platform that is NOT in
 * the set still renders its card — dimmed, with a "Coming soon" badge and a
 * disabled Connect button (see PlatformCard). Cards are never removed: the user
 * sees what is coming, they just can't start it.
 *
 * Shopify is the one conditional member (VIBE-1786). It is offered only when
 * the backend's runtime config says so, because the integration has to be
 * hidden while Shopify reviews the app and brought back by restarting the
 * container, without a rebuild. The App-Store-initiated install and its claim
 * (`?shopify=claim` in ConnectView, /shopify/claim) are a SEPARATE path and
 * keep working regardless — this gate is the connect grid only.
 */

import { useMemo } from 'react';
import { useConfigStore } from '../../stores/useConfigStore';

const ALWAYS_ACTIVE_PLATFORMS = [
  'meta-ads', 'google-ads', 'tiktok-ads', 'appsflyer', 'revenuecat', 'ga',
  'apple-search-ads', 'linkedin-ads',
] as const;

/** The active-platform set for this render. Identity is stable while the flag
 *  is, so callers may use it as a hook/memo dependency (PlatformGrid sorts the
 *  active platforms first with it). */
export function useActivePlatforms(): ReadonlySet<string> {
  const shopifyEnabled = useConfigStore((state) => state.shopifyIntegrationEnabled);

  return useMemo(() => {
    const active = new Set<string>(ALWAYS_ACTIVE_PLATFORMS);
    if (shopifyEnabled) active.add('shopify');
    return active;
  }, [shopifyEnabled]);
}
